/**
 * Phase 14.5 — QorBOM Docs · Quickstart page.
 *
 * The 5-line scan-from-scratch recipe (curl + Python + Go + Node)
 * plus the Try-It-Now sandbox widget. This is the default page
 * served at `/qorbom/api-docs` so a new visitor lands here.
 */
import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Download, Zap } from "lucide-react";

import QorbomDocsLayout from "../../components/qorbom-docs/QorbomDocsLayout";
import QorbomBrand      from "../../components/qorbom/QorbomBrand";
import TryItNow         from "../../components/qorbom-docs/TryItNow";
import { SectionHeader, NoticeBox, CodeTabs }
       from "../../components/qorbom-docs/DocsPrimitives";
import usePageMeta from "../../hooks/usePageMeta";
import M from "../../lib/pageMetaConfig";

const API = process.env.REACT_APP_BACKEND_URL;
const SANDBOX_BASE = `${API}`;


export default function QorbomDocsQuickstart() {
  usePageMeta(M.qorbomApiDocs);

  const baseExample = `${SANDBOX_BASE}/api/v1/cbom`;

  return (
    <QorbomDocsLayout testid="qorbom-docs-quickstart-page">
      <SectionHeader
        eyebrow="Get started · 5 minutes"
        title="Quickstart"
      />

      <p className="text-lg text-slate-600">
        Submit a scan, poll for completion, download the BOM. Five lines of
        code, two SDKs, zero setup.
      </p>

      <div className="mt-6 flex flex-wrap gap-3">
        <Link
          to="/qorbom/api-reference"
          className="inline-flex items-center gap-2 rounded-md bg-blue-800 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-blue-900"
          data-testid="qorbom-docs-quickstart-cta-reference"
        >
          <Zap className="h-4 w-4" /> Live API reference
        </Link>
        <a
          href={`${SANDBOX_BASE}/api/v1/cbom/openapi.json`}
          target="_blank" rel="noreferrer"
          className="inline-flex items-center gap-2 rounded-md border border-slate-300 px-5 py-2.5 text-sm font-semibold text-slate-900 transition-colors hover:bg-slate-50"
          data-testid="qorbom-docs-quickstart-cta-openapi"
        >
          <Download className="h-4 w-4" /> OpenAPI 3.1 JSON
        </a>
        <Link
          to="/qorbom/become-a-partner"
          className="inline-flex items-center gap-2 rounded-md border border-slate-300 px-5 py-2.5 text-sm font-semibold text-slate-900 transition-colors hover:bg-slate-50"
          data-testid="qorbom-docs-quickstart-cta-apply"
        >
          Apply for a key <ArrowRight className="h-4 w-4" />
        </Link>
      </div>

      <hr className="my-12 border-slate-200" />

      <h2 id="prerequisites" className="text-2xl font-bold tracking-tight text-slate-900"
          style={{ fontFamily: "Manrope, system-ui, sans-serif" }}>
        Prerequisites
      </h2>
      <p className="mt-3 text-slate-700">
        You need a <QorbomBrand /> Partner API key. New partners get a sandbox
        key (prefix <code>qb_test_</code>) within two business days of applying.
        See <Link to="/qorbom/api-docs/auth" className="text-blue-700 hover:underline">Authentication</Link> for
        the full key format + storage guidance.
      </p>

      <h2 id="three-steps" className="mt-10 text-2xl font-bold tracking-tight text-slate-900"
          style={{ fontFamily: "Manrope, system-ui, sans-serif" }}>
        Three calls, three minutes
      </h2>
      <p className="mt-3 text-slate-700">
        Pick a language. Every snippet below shows the exact same flow:
        submit a public GitHub URL, poll until done, fetch the BOM.
      </p>

      <CodeTabs
        testid="qorbom-docs-quickstart-tabs"
        tabs={[
          {
            label: "curl",
            lang: "bash",
            code: `# 1. Submit a scan
curl -X POST "${baseExample}/scans" \\
  -H "Authorization: Bearer $QORBOM_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{"repo_url": "https://github.com/octocat/Hello-World"}'

# 2. Poll (response status: queued → scanning → completed)
curl "${baseExample}/scans/cbom_xxxxxxxxxxxxxxxx" \\
  -H "Authorization: Bearer $QORBOM_KEY"

# 3. Download CycloneDX BOM (or ?format=spdx)
curl "${baseExample}/scans/cbom_xxxxxxxxxxxxxxxx/bom.json" \\
  -H "Authorization: Bearer $QORBOM_KEY" > bom.json`,
          },
          {
            label: "Python",
            lang: "python",
            code: `import os, time, requests

BASE = "${baseExample}"
HEADERS = {"Authorization": f"Bearer {os.environ['QORBOM_KEY']}"}

# 1. Submit
r = requests.post(f"{BASE}/scans", headers=HEADERS,
                  json={"repo_url": "https://github.com/octocat/Hello-World"})
scan_id = r.json()["id"]

# 2. Poll
while True:
    s = requests.get(f"{BASE}/scans/{scan_id}", headers=HEADERS).json()
    if s["status"] in ("completed", "failed"):
        break
    time.sleep(2)

# 3. Fetch BOM (CycloneDX 1.6)
bom = requests.get(f"{BASE}/scans/{scan_id}/bom.json", headers=HEADERS).json()
print(bom["bomFormat"], bom["specVersion"])     # CycloneDX 1.6`,
          },
          {
            label: "Node",
            lang: "javascript",
            code: `import axios from "axios";

const BASE = "${baseExample}";
const h = { Authorization: \`Bearer \${process.env.QORBOM_KEY}\` };

// 1. Submit
const { data: { id } } = await axios.post(\`\${BASE}/scans\`,
    { repo_url: "https://github.com/octocat/Hello-World" },
    { headers: h });

// 2. Poll
let scan;
do {
    await new Promise(r => setTimeout(r, 2000));
    ({ data: scan } = await axios.get(\`\${BASE}/scans/\${id}\`, { headers: h }));
} while (!["completed", "failed"].includes(scan.status));

// 3. BOM (CycloneDX 1.6)
const { data: bom } = await axios.get(\`\${BASE}/scans/\${id}/bom.json\`, { headers: h });
console.log(bom.bomFormat, bom.specVersion);`,
          },
          {
            label: "Go",
            lang: "go",
            code: `package main

import (
    "bytes"
    "encoding/json"
    "fmt"
    "net/http"
    "os"
    "time"
)

const BASE = "${baseExample}"

func main() {
    key := os.Getenv("QORBOM_KEY")
    body, _ := json.Marshal(map[string]string{"repo_url": "https://github.com/octocat/Hello-World"})
    req, _ := http.NewRequest("POST", BASE+"/scans", bytes.NewReader(body))
    req.Header.Set("Authorization", "Bearer "+key)
    req.Header.Set("Content-Type", "application/json")
    resp, _ := http.DefaultClient.Do(req)
    var created struct{ ID string ` + "`json:\"id\"`" + ` }
    json.NewDecoder(resp.Body).Decode(&created)
    fmt.Println("scan id:", created.ID)

    for {
        time.Sleep(2 * time.Second)
        sreq, _ := http.NewRequest("GET", BASE+"/scans/"+created.ID, nil)
        sreq.Header.Set("Authorization", "Bearer "+key)
        sresp, _ := http.DefaultClient.Do(sreq)
        var s struct{ Status string }
        json.NewDecoder(sresp.Body).Decode(&s)
        if s.Status == "completed" || s.Status == "failed" { break }
    }
}`,
          },
        ]}
      />

      <NoticeBox tone="blue" testid="qorbom-docs-quickstart-async-notice">
        <strong>Async scans.</strong> Scans run on a worker pool. Most public-repo
        scans take 20–30 seconds. Use webhooks (see{" "}
        <Link to="/qorbom/api-docs/webhooks" className="font-semibold text-blue-700 hover:underline">
          Webhooks
        </Link>
        ) to skip polling on production integrations.
      </NoticeBox>

      <hr className="my-12 border-slate-200" />

      <h2 id="try-it" className="text-2xl font-bold tracking-tight text-slate-900"
          style={{ fontFamily: "Manrope, system-ui, sans-serif" }}>
        Try it now — no signup
      </h2>
      <p className="mt-3 text-slate-700">
        Paste any public GitHub repo URL. We'll run a real <QorbomBrand /> scan
        and show you the readiness summary. One scan per IP per 24 hours.
      </p>

      <TryItNow />

      <hr className="my-12 border-slate-200" />

      <h2 id="next-steps" className="text-2xl font-bold tracking-tight text-slate-900"
          style={{ fontFamily: "Manrope, system-ui, sans-serif" }}>
        Where to next
      </h2>
      <ul className="mt-4 space-y-2 text-slate-700">
        <li>
          <Link to="/qorbom/api-docs/auth" className="font-medium text-blue-700 hover:underline">
            Authentication
          </Link>{" "}
          — key format, header, rotation, scopes
        </li>
        <li>
          <Link to="/qorbom/api-docs/endpoints" className="font-medium text-blue-700 hover:underline">
            Endpoints
          </Link>{" "}
          — every route + request/response schema
        </li>
        <li>
          <Link to="/qorbom/api-docs/webhooks" className="font-medium text-blue-700 hover:underline">
            Webhooks
          </Link>{" "}
          — push-style scan-complete notifications
        </li>
      </ul>
    </QorbomDocsLayout>
  );
}
